import type { AgentUsingBase, KnowledgeBase, LendingRequest } from '@ralph/contracts'
import { ACKS } from '@ralph/contracts'
import { Globe, MessageSquareOff } from 'lucide-react'
import { useId, useState } from 'react'
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/Dialog.tsx'
import { Button, Textarea } from '@/components/ui/primitives.tsx'
import { useDecideLendingRequest, useLendToAgent } from '@/hooks/use-knowledge-bases.ts'
import { useI18n } from '@/i18n/index.ts'
import { lendingAck } from './knowledge-base.ts'
import { asSentence, messageOf } from './knowledge-viewer.ts'

/**
 * Letting an agent answer from a knowledge base for everyone it reaches (PRD-26 D-16, D-17).
 *
 * The one decision on the base's page that widens who gets its contents without widening who can
 * use it, so it is taken on the facts rather than on a button label: who maintains the agent,
 * whether it can act, how many people it reaches today and how many of them cannot use the base,
 * that its reach grows without asking, and that stopping later does not take back what it said.
 * Those sentences are the dialog's body and — joined, verbatim — the `ackText` stored with the
 * decision, so the record says exactly what the maintainer read (AGM-042). Since PRD-27 D-7 the
 * values they were rendered from go with them, under the key and version of the catalogue entry
 * that worded them, so the same acknowledgement reads back in either language.
 *
 * The same dialog answers a request to allow it ("Allow"); declining one asks for a note, because
 * a refusal nobody explains is how people learn to stop asking rather than to ask better.
 */

export const LetAnswerDialog = ({
  base,
  entry,
  request,
  onClose
}: {
  base: Pick<KnowledgeBase, 'id' | 'name'>
  entry: AgentUsingBase
  /** Answering a request to allow it, rather than deciding unasked. */
  request?: LendingRequest
  onClose: () => void
}) => {
  // Inside the body, never at module scope (PRD-27 F-3, LOC-012).
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const titleId = useId()
  const lend = useLendToAgent(base.id)
  const decide = useDecideLendingRequest()
  // One shape, rendered once and stored once (PRD-27 D-7). The bullets on screen and the record
  // of what was agreed to come from the same parameters, so a replay in the other language is a
  // property of the code rather than a claim about two translations.
  const ackParams = lendingAck(entry.agent, entry.beyondAccess, base.name)
  const facts = t.lend.disclosure(fmt, ackParams)
  const ackText = facts.join(' ')
  const ack = {
    ackKey: ACKS.lending.key,
    ackVersion: ACKS.lending.version,
    ackLocale: fmt.locale,
    ackParams
  }
  const pending = lend.isPending || decide.isPending
  const error = lend.error ?? decide.error

  const confirm = () => {
    if (pending) return
    if (request) {
      decide.mutate({ requestId: request.id, decision: 'approved', ackText, ...ack }, { onSuccess: onClose })
    } else {
      lend.mutate({ agentId: entry.agent.id, ackText, ...ack }, { onSuccess: onClose })
    }
  }

  return (
    <Dialog
      titleId={titleId}
      icon={<Globe size={14} />}
      title={
        request ? t.lend.allowTitle(request.requestedBy.displayName, entry.agent.name) : t.lend.title(entry.agent.name)
      }
      lead={t.lend.lead(entry.agent.name, base.name)}
      onClose={onClose}
      width="lg"
    >
      <DialogBody>
        {request && request.note.trim() !== '' && (
          <figure className="rounded-tile border border-rule bg-fg/[0.02] px-3 py-2.5">
            <figcaption className="eyebrow">{t.lend.note(request.requestedBy.displayName)}</figcaption>
            <blockquote className="mt-1 text-pretty text-[13px] leading-[1.5]">{request.note}</blockquote>
          </figure>
        )}
        <ul className="space-y-2">
          {facts.map((fact) => (
            <li key={fact} className="flex items-start gap-2 text-pretty text-[13px] leading-[1.5]">
              <span aria-hidden className="mt-[8px] h-[5px] w-[5px] shrink-0 rounded-full bg-fg-subtle" />
              <span>{fact}</span>
            </li>
          ))}
        </ul>
        {error && (
          <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
            {asSentence(messageOf(error, t.genericError))}
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.lend.cancel}
        </Button>
        <Button variant="primary" disabled={pending} onClick={confirm}>
          {pending ? t.lend.working : request ? t.lend.allow : t.lend.confirm}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export const DeclineDialog = ({
  base,
  entry,
  request,
  onClose
}: {
  base: Pick<KnowledgeBase, 'name'>
  entry: AgentUsingBase
  request: LendingRequest
  onClose: () => void
}) => {
  const t = useI18n().t.knowledge
  const titleId = useId()
  const noteId = useId()
  const hintId = useId()
  const decide = useDecideLendingRequest()
  const [note, setNote] = useState('')
  const name = request.requestedBy.displayName

  return (
    <Dialog
      titleId={titleId}
      icon={<MessageSquareOff size={14} />}
      title={t.decline.title(name)}
      lead={t.decline.lead(entry.agent.name, base.name, name)}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (note.trim() === '' || decide.isPending) return
          decide.mutate({ requestId: request.id, decision: 'declined', note: note.trim() }, { onSuccess: onClose })
        }}
      >
        <DialogBody>
          <div>
            <label htmlFor={noteId} className="mb-1 block text-[13px] font-semibold">
              {t.decline.note}
            </label>
            <Textarea
              id={noteId}
              value={note}
              rows={3}
              maxLength={2000}
              required
              aria-describedby={hintId}
              onChange={(event) => setNote(event.target.value)}
            />
            <p id={hintId} className="mt-1 text-[12px] text-fg-subtle">
              {t.decline.noteHint}
            </p>
          </div>
          {decide.isError && (
            <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
              {asSentence(messageOf(decide.error, t.genericError))}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t.decline.cancel}
          </Button>
          <Button type="submit" variant="secondary" disabled={decide.isPending || note.trim() === ''}>
            {decide.isPending ? t.decline.working : t.decline.submit}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}
